import uniqid from 'uniqid';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { mc } from '@shakkerai/report';
import { translate } from '@shakkerai/util-i18n';
import { getCDNImage, getRemoteImageSize } from '@shakkerai/util-image';
import { requestUserStorage, requestModelVersionDetail } from '@shakkerai/services';
import { requestShakeImage } from '@shakkerai/services';
import {
  requestFirstLookParams,
  updatePromptCache,
  requestRecommendPrompt,
  requestStyleCodeParams,
} from '@shakkerai/services';
import { EVENT_LABEL_HISTORY_RELOAD } from '@shakkerai/pro-gen-history';
import { eventBus } from '@starflow/app/helpers';
import { notifyError, notifySuccess } from '@shakkerai/pro-components';
import { getCanvasState } from '@shakkerai/pro-store';
import { updateVipState } from '@shakkerai/pro-store';
import { getTempState, setTempState, getProjectState, updateTask } from '@starflow/app/store';
import { updateUnitData, getUnitData, getUnit } from '@starflow/app/helpers/flow';
import { BASETYPE_VALUE_TO_NAME } from '@shakkerai/pro-models';
import { MODEL_FLUX, SDXL_MODELS_FAMILY } from '@shakkerai/pro-models';
import { presetCheckpointsMap, fallbackModelBaseParams } from '@shakkerai/pro-models';
import { MODEL_SD15, MODEL_SDXL, BASETYPE_NAME_TO_VALUE } from '@shakkerai/pro-models';
import { samplerMethodOptions, simpleModeSupportedControlNets } from '@shakkerai/pro-constants';
import { controlNetConfig, initialGeneratorParams, defaultHiresOptions } from '@shakkerai/pro-constants';
import { loadImagesByPrompt } from '@shakkerai/pro-images';
import { syncVipInfo } from '@shakkerai/pro-commercial';
import { tryFixControlNetsForBaseType } from './reference';
import { checkAndPopNps } from '@shakkerai/utils-nps';
import { baseTypeToNumber, fixLoraWeight } from '@starflow/app/modules/models/helpers';
import { pickModelFields, pickVersionIntro } from '@starflow/app/modules/models/helpers';
import { ImageKey } from '@shakkerai/pro-images/src/store/state';
import { getElementGenInfo } from '@shakkerai/pro-hooks';
import {
  defaultGenerateParamsHandler,
  defaultGenerateResultHandler,
  defaultGenerateResumeHandler,
} from '../config/process';
import { calcMultiElementsRect } from '../../../helpers/element';

const idleStatus = { idle: true, success: true, error: true };
export const LoraCodeReg = /(?:#style:\s*(\w+))|(?:#([\u4e00-\u9fa5a-zA-Z]+))[\s,，。.]/i;
const MAX_UPSCALE_SIZE = 2048;

/**
 * 全部错误枚举
 * custom.bizError.power.notEnough=算力不足  1200000136
 * custom.bizError.power.freeze.error=算力预占失败  1200000137
 * custom.bizError.power.calculate.error=算力计算异常 1200000140
 * custom.bizError.space.notEnough.normal=图库空间不足，您可前往图库删除不需要的图片，或升级您的空间，一键扩容   1200000138
 * custom.bizError.space.notEnough.pro=图库空间不足，您可前往图库删除不需要的图片再进行生图 1200000139
 * custom.bizError.queue.priority.notEnough=加速次数不足 1200000141
 * custom.bizError.priority.freeze.error=加速次数冻结失败 1200000142
 * custom.bizError.model.count.notEnough=正在使用VIP专属模型 1200000146
 * custom.bizError.model.freeze.error=模型免费次数冻结失败 1200000147
 * custom.bizError.image.count.error=单次生图张数超限 1200000148
 * custom.bizError.image.tilediffusion.equity=无Tiled Diffusion权益 1200000149
 * custom.bizError.image.tilediffusion.layerdiffusion.equity=请勿同时开启layer diffusion和Tiled diffusion 1200000152
 * custom.bizError.comfy.xl.controlnet.equity=无XL ControlNet权益 1200000152
 */

// 需要付费的错误码枚举
export const sdErrCodes = [
  1200000136, 1200000137, 1200000140, 1200000141, 1200000142, 1200000146, 1200000148, 1200000149, 1200000138,
  1200000139, 1200000147, 1200000152,
];

export const mapSD3Params = (params) => {
  return {
    checkPointName: params.checkPointName,
    prompt: params.prompt,
    negPrompt: params.negativePrompt,
    promptRecommend: false,
    seed: params.seed,
    samplingMethod: params.samplingMethod,
    samplingStep: params.samplingStep,
    imgCount: params.imgCount,
    cfgScale: params.cfgScale,
    clipSkip: params.clipSkip,
    width: params.width,
    height: params.height,
    tiling: 0,
    randnSource: 1,
    restoreFaces: 0,
    hiresSteps: params.hiresSteps,
    denoisingStrength: params.denoisingStrength,
    // hiresModelId: [],
    // hiresType: null,
    hiResFix: 0,
    seedExtra: 0,
    resizeWidth: params.resizeWidth,
    resizeHeight: params.resizeHeight,
    hiresDenoisingStrength: params.hiresDenoisingStrength,
    maskImageId: params.maskImageId,
    sourceImageId: params.sourceImageId,
    maskBlur: params.maskBlur,
    maskedContent: params.maskedContent,
    maskPadding: params.maskPadding,
    imageMode: params.imageMode,
    maskMode: params.maskMode,
    resizeMode: params.resizeMode,
    inpaintArea: params.inpaintArea,
  };
};

const cachedControlnetParams = {};

export const calcPreprocessorResolution = (referenceSize, targetSize) => {
  const { width: referenceWidth, height: referenceHeight } = referenceSize;
  let { width: targetWidth, height: targetHeight } = targetSize;

  if (targetSize.hiResFixInfo) {
    targetWidth = targetSize.hiResFixInfo.resizeWidth;
    targetHeight = targetSize.hiResFixInfo.resizeHeight;
  }

  const k1 = targetWidth / referenceWidth;
  const k0 = targetHeight / referenceHeight;
  const estimation = Math.max(k0, k1) * Math.min(referenceWidth, referenceHeight);

  return Math.round(estimation);
};

export const switchGlobalMode = (globalMode) => {
  const currentGlobalMode = getTempState('globalMode');

  if (currentGlobalMode === globalMode) return;

  const updatedParams = {};
  const currentUnitData = getUnitData();

  localStorage.setItem('__MODE_SETTINGS_Key__', globalMode);

  if (globalMode === 'simple') {
    updatedParams.vae = initialGeneratorParams.primary.vae;

    if (Array.isArray(currentUnitData?.controlnets)) {
      updatedParams.controlnets = currentUnitData.controlnets.map((item) => {
        if (!item.type || simpleModeSupportedControlNets[item.type]) {
          return item;
        }

        cachedControlnetParams[item.id] = item;

        return Object.assign({}, item, { type: null });
      });
    }

    updatedParams.addOns = [];
    updateUnitData(null, updatedParams, { skipTracking: true });
  } else {
    if (Array.isArray(currentUnitData?.controlnets)) {
      const updatedParams = {
        controlnets: currentUnitData.controlnets.map((item) => {
          if (!item.type && cachedControlnetParams[item.id]) {
            return Object.assign({}, item, cachedControlnetParams[item.id]);
          }

          return item;
        }),
      };

      updateUnitData(null, updatedParams, { skipTracking: true });
    }
  }
  setTempState({ globalMode, showNormalCkptGide: false, exclusiveWindow: null });
};

export const restoreGenerateParams = (unitId) => {
  const unit = getUnit(unitId);
  const unitType = unit.type === 'generator' ? 'primary' : unit.type;
  const restoreParams = Object.assign({}, initialGeneratorParams[unitType] || {});

  if (unitType === 'inpaint') {
    delete restoreParams.presetBaseModelId;

    const originalParams = pick(unit.data.originalParams, [
      'presetBaseModelId',
      'samplingSteps',
      'samplerMethod',
      'baseType',
      'cfgScale',
      'vae',
    ]);

    if (originalParams.baseType) {
      restoreParams.baseType = originalParams.baseType;
    }

    if (originalParams.cfgScale) {
      restoreParams.cfgScale = originalParams.cfgScale;
    }

    if (originalParams.samplerMethod) {
      restoreParams.samplerMethod = originalParams.samplerMethod;
    }

    if (originalParams.samplingSteps) {
      restoreParams.samplingSteps = originalParams.samplingSteps;
    }

    if (originalParams.presetBaseModelId) {
      restoreParams.presetBaseModelId = originalParams.presetBaseModelId;
    }

    if (originalParams.vae) {
      restoreParams.vae = originalParams.vae;
    }
  }

  updateUnitData(unitId, restoreParams);
  // 把图转词也清空
  setTempState({
    imagePromptElement: {
      url: null,
      id: null,
    },
  });
};

export const shouldApplyHiddenStrategy = ({ projectData }) => {
  // 暂时只在海外版启用暗策略
  // if (!isOverseas) return false;

  if (!projectData) return false;

  // 只有Flux模型才启用暗策略
  // if (projectData.baseType !== MODEL_FLUX) return false;

  // 只选择1张图，不启用暗策略
  // if (projectData.count === 1) return false;

  // 只有通用预设才启用暗策略
  if (projectData.presetBaseModelId !== 'base_checkpoint') {
    return false;
  }

  // 使用了CN，不启用暗策略(开启)
  if (projectData.controlnets?.length) return false;

  // 使用了add on，不启用暗策略
  if (projectData.addOns?.length) return false;

  // 如果是细微变化生图，也不触发暗策略
  if (projectData.isDetailsVary) return false;
  if (projectData.isOverallVary) return false;

  // 兼容旧版数据
  if (projectData.isRemix) return false;

  const realLoras = projectData.isStyleExtend ? [] : projectData.loraModels || []; //?.filter((item) => !item.isHiddenStrategy);

  // 使用了lora模型，不启用暗策略
  if (realLoras?.length >= 1) return false;

  // 没使用预设
  if (!projectData.presetBaseModelId) {
    if (!projectData.checkpoint) return false;

    // 使用的底模不是写实摄影，不启用暗策略
    if (!projectData.checkpoint?.versionId !== presetCheckpointsMap['base_checkpoint'].versionDetail?.versionId) {
      return false;
    }
  }

  return true;
};

export const hasLoraCode = ({ projectData }) => {
  const { prompt } = projectData;

  // 只有通用预设才启用
  if (projectData.presetBaseModelId !== 'base_checkpoint') {
    return false;
  }

  return LoraCodeReg.test(prompt);
};

export const fetchHiddenStrategyParams = async (params) => {
  const { coreParamsField } = params;
  const { count, prompt, loraModels, baseType, isStyleExtend, extendedPrompt, presetBaseModelId, checkpoint } =
    params.projectData;

  let modelId, versionUuid;

  if (presetBaseModelId) {
    modelId = presetCheckpointsMap[presetBaseModelId].versionDetail.modelId;
    versionUuid = presetCheckpointsMap[presetBaseModelId].versionDetail.uuid;
  } else if (checkpoint) {
    modelId = checkpoint.modelId;
    versionUuid = checkpoint.uuid;
  } else {
    return null;
  }

  const requestParams = {
    modelId,
    versionUuid,
    baseType,
    modelType: 1,
    prompt: prompt,
  };

  if (isStyleExtend && loraModels) {
    requestParams.latestVersionUuids = loraModels?.map((item) => item.uuid);
  }

  const result = await requestFirstLookParams(requestParams).then((res) => res.data.modelCombinations);

  mc('tool.shadow.res', { strategy: result, shadow_id: params.uuid });

  if (!Array.isArray(result) || !result.length) return null;

  const paramsArray = result.map((item) => {
    const loraModels = item.loraList.map((item) => ({
      ...pickModelFields(item.modelVersion || {}, true),
      isHiddenStrategy: true,
      weight: typeof item.loraWeight === 'number' ? item.loraWeight : 0.8,
    }));

    const loraPrompts = loraModels.map((item) => item.triggerWord).join(',');
    const combinedPrompt = `${loraPrompts ? loraPrompts + ',' : ''}${extendedPrompt || prompt}`;

    const data = {
      params: {
        // checkpointId: item.modelVersion?.versionId,
        additionalNetwork: loraModels.map((item) => ({
          modelId: item?.versionId,
          weight: fixLoraWeight(item.weight),
          type: 0,
        })),
      },
      projectData: {
        // checkpoint: item.modelVersion ? pickModelFields(item.modelVersion) : null,
        // checkpointId: item.modelVersion?.versionId,
        loraModels: loraModels,
      },
    };

    if (coreParamsField && params[coreParamsField]) {
      data.params[coreParamsField] = Object.assign({}, params[coreParamsField], {
        imgCount: 1,
        prompt: combinedPrompt,
      });
    }

    if (!data.projectData.checkpoint) {
      delete data.projectData.checkpoint;
      delete data.params.checkpointId;
    }

    return data;
  });

  if (paramsArray.length >= count) {
    return paramsArray.slice(0, count);
  }

  // 如果推荐数量不够，则重复数组补全到目标数量
  for (let i = 0; i < count; i++) {
    paramsArray.push(paramsArray[i % paramsArray.length]);
  }

  return paramsArray;
};

export const handleStyleLoraCode = async (params) => {
  // style code的逻辑，如果prompt提示词包含了code特征，取出code；后续会优化
  const { prompt, loraModels, extendedPrompt } = params.projectData;
  const { coreParamsField } = params;

  let codeArr = LoraCodeReg.exec(prompt);

  const code = codeArr?.[1] || codeArr?.[2];

  const requestParams = {
    code: code,
  };

  const result = await requestStyleCodeParams(requestParams).then((res) => res.data.modelCombinations);

  if (!Array.isArray(result) || !result.length) return null;

  const item = result[0];
  const loraModelsByCode = item.loraList.map((_item) => ({
    ...pickModelFields(_item.modelVersion || {}, true),
    weight: typeof _item.loraWeight === 'number' ? _item.loraWeight : 0.8,
  }));

  const restLoraLen = 4 - loraModels?.length;

  if (restLoraLen < 1 || !loraModelsByCode?.length) {
    return false;
  }

  const usedCodeLoras = loraModelsByCode.slice(0, restLoraLen) || [];
  const loraPrompts = usedCodeLoras.map((item) => item.triggerWord).join(',');
  let combinedPrompt = `${loraPrompts ? loraPrompts + ',' : ''}${extendedPrompt || prompt}`;
  combinedPrompt = combinedPrompt?.replace(LoraCodeReg, '').replace(/^,/g, '')?.replace(/[,]+/g, ',');
  params.prompt = combinedPrompt;
  params.projectData.prompt = prompt?.replace(LoraCodeReg, '')?.replace(/^,/g, '')?.replace(/[,]+/g, ',');

  if (coreParamsField && params[coreParamsField]) {
    params[coreParamsField] = Object.assign({}, params[coreParamsField], {
      prompt: combinedPrompt,
    });
  }

  const AllLoraModels = [...(loraModels || []), ...usedCodeLoras];

  // 去重
  const uniqueLoraModels = AllLoraModels.filter((item, index, self) => {
    return self.findIndex((t) => t?.versionId === item?.versionId) === index;
  });

  // params.projectData.styleCode = code;
  params.additionalNetwork = uniqueLoraModels.map((item) => ({
    modelId: item?.versionId,
    weight: fixLoraWeight(item.weight),
    type: 0,
  }));

  return true;
};

export const handleTaskUpdate = (taskLocalId) => {
  return (data, subTaskLocalId) => {
    if (!data) return;

    updateTask(taskLocalId, (taskData) => {
      const nextTaskData = Object.assign({}, taskData);

      if (subTaskLocalId) {
        nextTaskData.results = nextTaskData.results.map((item) => {
          if (item.id !== subTaskLocalId) return item;

          const nextItem = Object.assign({}, item);

          if (data.percentCompleted > 0 || data.subStatus >= 1) {
            if (taskData?.status === 'error') {
              nextItem.progress = -1;
            } else {
              nextItem.progress = data.percentCompleted;
              nextItem.status = 'generating';
            }
          } else if (data.queueStatus === 1) {
            nextItem.status = 'queueing';
            nextItem.queueLength = taskData.queueLength || data.totalQueueNum;
            nextItem.queueIndex = Math.min(data.queueNum, taskData.queueLength || data.queueNum);
            nextItem.isAccelerating = data.taskQueuePriority === 2; // 1 普通队列 2加
          }

          return nextItem;
        });
      } else {
        if (data.percentCompleted > 0 || data.subStatus >= 1) {
          if (taskData?.status === 'error') {
            nextTaskData.progress = -1;
          } else {
            nextTaskData.progress = data.percentCompleted;
            nextTaskData.status = 'generating';
          }
        } else if (data.queueStatus === 1) {
          nextTaskData.status = 'queueing';
          nextTaskData.queueLength = taskData.queueLength || data.totalQueueNum;
          nextTaskData.queueIndex = Math.min(data.queueNum, taskData.queueLength || data.queueNum);
          nextTaskData.isAccelerating = data.taskQueuePriority === 2; // 1 普通队列 2加速队列
        }
      }

      return nextTaskData;
    });
  };
};

export const findNextTaskPosition = () => {
  const { elements, tasks } = getProjectState();
  const { fe_queue, map, current } = tasks;

  const rootElements = elements.filter((item) => !item.parentId && item.type !== 'generator');
  const elementsRect = calcMultiElementsRect(rootElements);

  let x = 0;
  let y = 0;

  const currentTask = map[current];
  const lastLocatedQueue = fe_queue.findLast((item) => typeof item.height === 'number' && typeof item.y === 'number');

  if (lastLocatedQueue) {
    // 有任务队列，就跟在最后一个任务队列后面
    // x = lastLocatedQueue.x;
    y = lastLocatedQueue.y + lastLocatedQueue.height + 32;
  } else if (currentTask && typeof currentTask.x === 'number' && typeof currentTask.height == 'number') {
    // x = currentTask.x;
    y = currentTask.y + currentTask.height + 32;
  } else if (elementsRect.height) {
    // x = elementsRect.x;
    y = elementsRect.y + elementsRect.height + 32;
  }

  return { x, y };
};

export const updateHiresOptions = (unitId, data) => {
  updateUnitData(unitId, {
    hiresOptions: Object.assign({}, defaultHiresOptions, getUnitData(unitId).hiresOptions, data),
  });
};

const fixGenerateSizeForSDXL = (width, height) => {
  let scale = Math.sqrt((width * height) / (1024 * 1024));

  let fixedWidth = Math.round(width / scale);
  let fixedHeight = Math.round(height / scale);

  let newFixedWidth = fixedWidth - (fixedWidth % 8);
  let newFixedHeight = fixedHeight - (fixedHeight % 8);

  scale *= fixedWidth / newFixedWidth;

  return {
    width: newFixedWidth,
    height: newFixedHeight,
    scale: scale,
  };
};

export const fixHiresOptions = (unitId, trigger) => {
  const { baseType, width, height, hiresOptions } = getUnitData(unitId);

  let shouldEnableHires = null;
  let hiresScale = 1;
  let generateWidth = width;
  let generateHeight = height;

  // 对于非SD15和SDXL的模型，不支持高清修复
  if (!SDXL_MODELS_FAMILY[baseType] && baseType !== MODEL_SD15) {
    updateUnitData(unitId, {
      hiresOptions: Object.assign({}, defaultHiresOptions, hiresOptions, { enabled: false }),
    });

    return;
  }

  if (trigger === 'generateSize' || trigger === 'baseType') {
    // 根据生图尺寸自动计算scale和sourceSize
    if (baseType === MODEL_SD15) {
      if (
        (width >= 512 && width <= 768 && height > 768) ||
        (height >= 512 && height <= 768 && width > 768) ||
        (width > 768 && height > 768)
      ) {
        shouldEnableHires = true;
        hiresScale = Math.max(width, height) / 768;

        if (width > height) {
          generateWidth = 768;
          generateHeight = Math.round(height / hiresScale);
        } else {
          generateHeight = 768;
          generateWidth = Math.round(width / hiresScale);
        }
      }
    } else {
      if (
        (width >= 512 && width <= 768 && height > 768) ||
        (height >= 512 && height <= 768 && width > 768) ||
        (width > 768 && height > 768)
      ) {
        if (width * height > 1024 * 1024) {
          const fixedSize = fixGenerateSizeForSDXL(width, height);

          generateWidth = fixedSize.width;
          generateHeight = fixedSize.height;

          shouldEnableHires = fixedSize.scale > 1;
          hiresScale = fixedSize.scale;
        }
      }
    }

    if (shouldEnableHires) {
      // 如果命中高清策略，就自动开启高清修复选项
      const nextHiresOptions = {
        enabled: true,
        scale: Number(hiresScale.toFixed(2)),
        width: generateWidth,
        height: generateHeight,
      };

      if (trigger === 'generateSize') {
        getCanvasState().updateOpenedSubWindow('hires-options');
      }

      updateUnitData(unitId, {
        hiresOptions: Object.assign({}, defaultHiresOptions, hiresOptions || {}, nextHiresOptions),
      });
    } else {
      updateUnitData(unitId, {
        hiresOptions: Object.assign({}, defaultHiresOptions, hiresOptions || {}, {
          scale: 1,
          width: generateWidth,
          height: generateHeight,
        }),
      });
    }
  } else if (trigger === 'sourceSize') {
    // 根据源图尺寸自动计算generateSize和scale
    hiresScale = hiresOptions.scale;
    generateWidth = hiresOptions.width * hiresOptions.scale;
    generateHeight = hiresOptions.height * hiresOptions.scale;

    if (Math.max(generateWidth, generateHeight) > MAX_UPSCALE_SIZE) {
      const ratio = Math.max(generateWidth, generateHeight) / MAX_UPSCALE_SIZE;

      hiresScale = Math.round(ratio * 100) / 100;
      generateWidth = Math.round(generateWidth / ratio);
      generateHeight = Math.round(generateHeight / ratio);
    }

    if (SDXL_MODELS_FAMILY[baseType] && generateWidth * generateHeight > 1024 * 1024) {
      const fixedSize = fixGenerateSizeForSDXL(generateWidth, generateHeight);

      generateWidth = fixedSize.width;
      generateHeight = fixedSize.height;
      hiresScale = fixedSize.scale;
    }

    const updatedParams = {
      hiresOptions: Object.assign({}, hiresOptions || {}, {
        scale: Number(hiresScale.toFixed(2)),
      }),
    };

    if (hiresOptions.enabled) {
      updatedParams.width = Math.round(generateWidth);
      updatedParams.height = Math.round(generateHeight);
    }

    updateUnitData(unitId, updatedParams);
  } else if (trigger === 'scale') {
    if (hiresOptions.enabled) {
      const updateParams = {};
      updateParams.width = Math.round(hiresOptions.width * hiresOptions.scale);
      updateParams.height = Math.round(hiresOptions.height * hiresOptions.scale);
      updateUnitData(unitId, updateParams);
    }
  } else if (trigger === 'toggle') {
    if (!hiresOptions.width || !hiresOptions.height || !hiresOptions.scale) {
      return;
    }

    // 如果是开启或者关闭高清修复选项，需要恢复生图尺寸
    if (hiresOptions.enabled) {
      updateUnitData(unitId, {
        width: Math.round(hiresOptions.width * hiresOptions.scale),
        height: Math.round(hiresOptions.height * hiresOptions.scale),
      });
    } else {
      updateUnitData(unitId, {
        width: hiresOptions.width,
        height: hiresOptions.height,
      });
    }
  }
};

export const applyGenerateParams = async (params, extra, accepts = 'all', unitId) => {
  const isSimpleMode = getTempState('globalMode') === 'simple';
  const currentParams = getUnitData(unitId);
  const currentBaseType = currentParams.baseType;

  let importData = {
    styleIcon: null,
  };

  if (accepts === 'all') {
    if (params.styleCode) {
      params.prompt = `${params.styleCode}, ${params.prompt}`;
    }
    importData = { ...params, ...extra };
    importData.width = params.renderWidth || params.width;
    importData.height = params.renderHeight || params.height;
  }

  if (accepts === 'all' || accepts === 'prompt') {
    importData.prompt = params.prompt;
    importData.negativePrompt = params.negativePrompt;
    importData.extendedPrompt = '';

    if (params.prompt && params.extendedPrompt) {
      updatePromptCache(params.prompt, params.extendedPrompt);
    }
  }

  if (accepts === 'styleCode' && params.currentValue) {
    if (!currentParams?.prompt.includes(params.styleCode)) {
      importData.prompt = `#style: ${params.currentValue}, ${currentParams.prompt}`;
    }
  }

  if (accepts === 'all' || accepts === 'style' || accepts === 'models' || accepts === 'checkpoint') {
    if (params.presetBaseModelId) {
      importData.checkpoint = null;
      importData.presetBaseModelId = params.presetBaseModelId;
    } else if (params.checkpoint) {
      importData.presetBaseModelId = null;
      importData.baseType = params.baseType;
      importData.checkpoint = { ...params.checkpoint };

      if (importData.checkpoint) {
        importData.checkpoint.baseType =
          BASETYPE_VALUE_TO_NAME[importData.checkpoint.baseType] || importData.checkpoint.baseType;
      }
    }
  }

  if (accepts === 'all' || accepts === 'style' || accepts === 'models' || accepts === 'loras') {
    if (Array.isArray(params.loraModels)) {
      if (accepts === 'style' || accepts === 'all') {
        importData.styleIcon = extra?.styleIcon;
        importData.loraModels = params.loraModels.map((item) => {
          return pickModelFields({
            ...item,
            baseType: BASETYPE_VALUE_TO_NAME[item.baseType] || item.baseType,
          });
        });
      } else {
        importData.loraModels = params.loraModels
          .filter((item) => !item.isHiddenStrategy)
          .map((item) => {
            return pickModelFields({
              ...item,
              baseType: BASETYPE_VALUE_TO_NAME[item.baseType] || item.baseType,
            });
          });
      }
    }
  }

  if ((accepts === 'all' || accepts === 'references') && params.controlnets?.length) {
    let referenceImage = params.reference?.image;

    if (params?.generateType === 'inpaint') {
      if (params.sourceImage) {
        referenceImage = params.sourceImage;
      } else if (extra.img2img) {
        // 历史数据兼容
        try {
          const sourceImageId = extra.img2img?.sourceImageId;
          const sourceImageUrl = getCDNImage(sourceImageId.replace('copy/', ''));
          const size = await getRemoteImageSize(sourceImageUrl);

          referenceImage = {
            id: uniqid(),
            url: sourceImageUrl,
            width: size.width,
            height: size.height,
          };
        } catch {
          // ...
        }
      }
    }

    importData.controlnets = params.controlnets
      .filter((item) => {
        if (!controlNetConfig[item.type]) return false;
        // if (!controlNetConfig[item.type][importData.baseType]) return false;
        if (!item.sourceImage?.url) return false;

        return true;
      })
      .map((item) => {
        if (!item.id) item.id = uniqid();
        if (item.controlWeight > 1) item.controlWeight = Number((item.controlWeight / 100).toFixed(2));

        return item;
      });

    if (referenceImage?.url) {
      importData.controlnets = importData.controlnets.map((item) => {
        item.sourceImage = item.sourceImage || referenceImage;
        return item;
      });

      let referenceScale = params.reference?.scale || 50;

      importData.imageReference = {
        id: referenceImage.id || uniqid(),
        url: referenceImage.url,
        width: referenceImage.width,
        height: referenceImage.height,
        label: referenceImage.label,
        strength: referenceScale,
        resizeMode: 1,
        mode: 'custom',
      };
    }
  }

  if (accepts === 'all' || accepts === 'addons') {
    importData.addOns = params.addOns || [];
  }

  if (accepts === 'all' || accepts === 'params') {
    let samplingSteps = Number(params.samplingSteps);

    if (samplingSteps < 1) {
      samplingSteps = 1;
    } else if (isNaN(samplingSteps)) {
      samplingSteps = 20;
    }

    if (isSimpleMode || accepts === 'all') {
      importData.seedType = '0';
    } else {
      importData.seedType = params.seedNumber ? '1' : '0';
      importData.seedNumber = params.seedNumber;
    }

    importData.samplerMethod = params.samplerMethod;
    importData.samplingSteps = samplingSteps;
  }

  delete importData.reference;
  delete importData.sourceImage;
  delete importData.projectData;

  if (!importData.samplerMethod) {
    delete importData.samplerMethod;
  }

  if (!importData.seedNumber) {
    delete importData.seedNumber;
  }

  if (typeof importData.baseType !== 'undefined') {
    importData.baseType =
      typeof importData.baseType === 'number'
        ? importData.baseType
        : BASETYPE_NAME_TO_VALUE[importData.baseType] || MODEL_SDXL;
  }

  if (accepts === 'all' || accepts === 'models' || accepts === 'checkpoint' || accepts === 'loras') {
    setTempState({ selectedEnhanceStyle: null });
  }

  if (importData.checkpoint) {
    importData.baseType = BASETYPE_NAME_TO_VALUE[importData.checkpoint.baseType] || MODEL_SDXL;

    try {
      // 查询模型的结构化信息
      const versionData = await requestModelVersionDetail(importData.checkpoint.uuid).then((res) => res.data);
      const versionIntro = Object.assign(
        {},
        fallbackModelBaseParams[importData.baseType],
        pickVersionIntro(versionData),
      );

      if (versionIntro.cfg) {
        if (!importData.cfgScale) {
          if (versionIntro.cfgScale) importData.cfgScale = versionIntro.cfgScale;
          if (versionIntro.cfg) importData.cfgScale = versionIntro.cfg;
        }

        if (!importData.samplingSteps) {
          if (versionIntro.samplingSteps) importData.samplingSteps = versionIntro.samplingSteps;
        }

        if (!importData.samplerMethod) {
          if (versionIntro.samplerMethod) importData.samplerMethod = versionIntro.samplerMethod;
          if (versionIntro.samplerMethods?.[0] >= 0) importData.samplerMethod = String(versionIntro.samplerMethods[0]);
        }
      }

      if (versionIntro.triggerWord?.length) {
        importData.checkpoint.triggerWord = versionIntro.triggerWord.join(',');
      }

      if (versionIntro.ngPrompt) {
        importData.checkpoint.ngPrompt = versionIntro.ngPrompt;
      }
    } catch {
      //...
    }
  } else {
    // 如果没有底模
    // - 如果选了预设模型，且没有loras，则使用预设模型的baseType
    // - 如果选了预设模型，且有loras，则使用第一个支持预设模型的loraModels的baseType
    // - 如果没有预设模型，且有loras，则使用第一个loraModels的baseType
    // - 如果没有预设模型，且没有loras，则使用默认的baseType

    let loraBaseType = null;
    let presetBaseModelData = null;
    let loraBaseTypes = [];

    if (importData.loraModels?.length) {
      loraBaseTypes = importData.loraModels.map((item) => baseTypeToNumber(item.baseType) || MODEL_FLUX);
      loraBaseType = loraBaseTypes[0];
    }

    if (importData.presetBaseModelId) {
      presetBaseModelData = presetCheckpointsMap[importData.presetBaseModelId];
    }

    if (!presetBaseModelData) {
      if (loraBaseType) {
        importData.baseType = loraBaseType;
      }
    } else {
      if (BASETYPE_NAME_TO_VALUE[presetBaseModelData.versionDetail.baseType] === loraBaseType) {
        importData.baseType = loraBaseType;
      } else if (loraBaseTypes.length) {
        importData.baseType = loraBaseTypes[0];
      } else {
        importData.baseType = importData.baseType || MODEL_FLUX;
      }
    }
  }

  if (importData.baseType) {
    if (importData.baseType !== currentBaseType) {
      const fallbackParams = fallbackModelBaseParams[importData.baseType];

      if (fallbackParams) {
        if (!importData.samplerMethod && fallbackParams.samplerMethod) {
          importData.samplerMethod = fallbackParams.samplerMethod;
        }

        if (!importData.cfgScale && fallbackParams.cfgScale) {
          importData.cfgScale = fallbackParams.cfgScale;
        }
      }
    }

    if (importData.loraModels) {
      importData.loraModels = importData.loraModels.filter((item) => {
        return baseTypeToNumber(item.baseType) === importData.baseType;
      });
    }
  }

  console.log(`[DEBUG] importData`, importData);

  updateUnitData(unitId, importData);
  tryFixControlNetsForBaseType(unitId);

  if (accepts === 'all') {
    fixHiresOptions(unitId, 'generateSize');
  }
};

export const checkIfWithFaceCN = (controlnets) => {
  if (!Array.isArray(controlnets)) return false;
  if (!controlnets.length) return false;

  return controlnets.some((item) => {
    if (item.type !== 'face') return false;
    if (!item.sourceImage?.url) return false;
    if (item.referenceImage?.url) return true;
    if (item.presetReferenceImageId) return true;

    return false;
  });
};

export const pickModelsFromSouceElement = async (sourceElement) => {
  const genInfo = await getElementGenInfo(sourceElement);
  const baseType = genInfo?.baseType || MODEL_SDXL;
  const checkpoint = genInfo?.checkpoint || null;
  const loraModels = genInfo?.loraModels || [];
  const presetBaseModelId = genInfo?.presetBaseModelId || null;

  return { checkpoint, loraModels, baseType, presetBaseModelId };
};

export const generateParamsHandlers = {
  generator: defaultGenerateParamsHandler,
};

export const generateResultHandlers = {
  generator: defaultGenerateResultHandler,
};

export const generateResumeHandlers = {
  generator: defaultGenerateResumeHandler,
};

export const registerGenerateParamsHandler = (type, handler, override) => {
  if (generateParamsHandlers[type] && !override) {
    return;
  }

  generateParamsHandlers[type] = handler;
};

export const registerGenerateResultHandler = (type, handler, override) => {
  if (generateResultHandlers[type] && !override) {
    return;
  }

  generateResultHandlers[type] = handler;
};

export const registerGenerateResumeHandler = (type, handler, override) => {
  if (generateResumeHandlers[type] && !override) {
    return;
  }

  generateResumeHandlers[type] = handler;
};

export const prepareGenerateParams = async ({ type = 'generator', params, isTryClacMode = false }) => {
  if (generateParamsHandlers[type]) {
    return await generateParamsHandlers[type]({ type, params, isTryClacMode });
  }

  throw new Error(`Unknown generate params handler: ${type}`);
};

export const getCurrentGenerateParams = async (generatorId) => {
  const unit = getUnit(generatorId);
  return await prepareGenerateParams({ type: unit.type, params: unit.data, isTryClacMode: true });
};

export const resumeGeneration = async (task) => {
  const resumeHandler = generateResumeHandlers[task.type] || generateResumeHandlers.generator;
  return await resumeHandler(task);
};

export const submitGeneration = async (task) => {
  let taskLocalId = task.id;
  let generateType = task.type;
  let sourceParams = task.params;

  console.log(`[DEBUG] task`, task);

  if (sourceParams.prompt && sourceParams.prompt.trim() === 'SHAKKER_COMMAND_ENABLE_DEBUG') {
    globalThis.STARFLOW_DEBUG_ENABLED = true;
    notifySuccess('调试模式已开启，刷新页面可关闭');
    return;
  }

  let fullPayload;

  if (sourceParams.prompt) {
    loadImagesByPrompt(sourceParams.prompt, ImageKey.recommendImages, true);
  }

  try {
    fullPayload = await prepareGenerateParams({
      type: generateType,
      params: sourceParams,
    });

    console.log(`[DEBUG] fullPayload`, fullPayload);

    if (!fullPayload) {
      return;
    }
  } catch (error) {
    console.error(error);
    notifyError(translate('generator_failed'), error.message || error.msg || error);
    return;
  }

  const projectData = fullPayload.projectData;
  const generateCount = projectData.count;

  const { threshold, usedCount } = await requestUserStorage()
    .then((res) => res.data || { threshold: -1, usedCount: 0 })
    .catch(() => ({ threshold: -1, usedCount: 0 }));

  if (threshold === -1) {
    throw new Error('error_storage_check_failed');
  }

  if (usedCount + generateCount > threshold) {
    notifyError(translate('history_storagefull'), translate('history_storagefull_tip'));
    return;
  }

  const results = Array.from({ length: generateCount }, () => ({
    id: uniqid(),
    url: null,
  }));

  const taskData = {
    hasSubTasks: false,
    results: results,
    count: generateCount,
    projectData: fullPayload.projectData,
    width: projectData.renderWidth,
    height: projectData.renderHeight,
  };

  updateTask(taskLocalId, taskData);

  if (generateType === 'generator') {
    let extendedPrompt;

    let hasRealLoras = false;
    if (hasLoraCode(fullPayload)) {
      hasRealLoras = await handleStyleLoraCode(fullPayload);
    }

    if (fullPayload.projectData.promptRecommend) {
      try {
        // 前端进行提示词扩写
        extendedPrompt = await requestRecommendPrompt(hasRealLoras ? fullPayload?.prompt : projectData.prompt);

        const enabledLoraModels = projectData.loraModels?.filter(
          (lora) => baseTypeToNumber(lora.baseType) === projectData.baseType,
        );

        fullPayload.projectData.extendedPrompt = extendedPrompt;

        const loraPrompts = enabledLoraModels?.map((lora) => lora.triggerWord).join(',');
        const fullPrompt = `${loraPrompts ? loraPrompts + ',' : ''}${extendedPrompt}`;

        if (fullPayload.coreParamsField) fullPayload[fullPayload.coreParamsField].prompt = fullPrompt;

        mc('tool.shadow.refine', {});
      } catch (error) {
        console.error(error);
      }
    }

    // 对于主生成器任务，判断是否需要获取暗策略参数(firstlook需求)

    let hasSubTasks = false; // 12.2日，小光那边的需求，所有任务都通过子任务的方式来实现并发运行，而不是仅限于暗策略生图任务～
    let results = taskData.results.concat();

    try {
      // 流模式里的生成器手动发起的任务不走并发策略
      if (!task.isFlowManualGenerator) {
        // 非手动发起的生成器任务, 使用暗策略
        if (shouldApplyHiddenStrategy(fullPayload) && !hasRealLoras) {
          await mc('tool.shadow.judge', {
            shadow_id: fullPayload.uuid,
            prompt: extendedPrompt, // 扩写后的提示词
            ori_prompt: projectData.prompt, // 原始提示词
            is_preset: projectData.presetBaseModelId ? 'y' : 'n',
            'sd-generate-req': omit(fullPayload, ['projectData']), // 生图参数
          });

          // 使用暗策略生图参数
          const hiddenStrategyParams = await fetchHiddenStrategyParams(fullPayload);

          if (Array.isArray(hiddenStrategyParams)) {
            fullPayload.subTasks = [];
            hasSubTasks = true;

            hiddenStrategyParams.forEach((item, index) => {
              if (results[index]) {
                results[index].taskId = null;
                results[index].status = 'pending';
                results[index].progress = 0;
                results[index].params = item;

                fullPayload.subTasks[index] = {
                  id: results[index].id,
                  params: item,
                };
              }
            });
          }
        } else if (true) {
          // 如果不需要并发执行，这里改为false即可

          fullPayload.subTasks = [];
          hasSubTasks = true;

          const params = {
            projectData: { count: 1 },
            params: {
              [fullPayload.coreParamsField]: {
                ...fullPayload[fullPayload.coreParamsField],
                imgCount: 1,
              },
            },
          };

          Array.from({ length: projectData.count }).forEach((_, index) => {
            if (results[index]) {
              results[index].taskId = null;
              results[index].status = 'pending';
              results[index].progress = 0;
              results[index].params = params;

              fullPayload.subTasks[index] = {
                id: results[index].id,
                params: params,
              };
            }
          });
        }
      }

      updateTask(taskLocalId, { hasSubTasks, results });
    } catch (error) {
      console.log(`[DEBUG] Unable to fetch hidden strategy params`, error);
    }
  }

  // 填充基本数据

  mc('tool.shake', {
    shadow_id: fullPayload.uuid,
    original_prompt: projectData.prompt,
    mode: projectData.isSimpleMode ? 'simple' : 'pro',
    'sd-generate-req': omit(fullPayload, ['projectData']),
  });

  let taskId = null;

  try {
    const result = await new Promise((resolve, reject) => {
      requestShakeImage(fullPayload, {
        onStart: (_taskId, subTaskLocalId) => {
          taskId = _taskId;

          if (subTaskLocalId) {
            updateTask(taskLocalId, (taskData) => {
              const nextTaskData = { ...taskData };

              nextTaskData.results = taskData.results.map((item) => {
                if (item.id !== subTaskLocalId) return item;
                return { ...item, taskId };
              });

              if (!nextTaskData.taskId) {
                nextTaskData.taskId = taskId;
              }

              return nextTaskData;
            });
          } else {
            updateTask(taskLocalId, { taskId });
          }

          syncVipInfo();
        },
        onError: (error) => {
          if (sdErrCodes.includes(error.code)) {
            // 试用次数不足的case
            updateVipState({
              proPurchaseModalVisible: true,
              currentErrorCode: error.code,
              currentErrorMsg: error.message,
            });
          }

          let allTaskFailed = false;

          if (error.subTaskLocalId) {
            updateTask(taskLocalId, (taskData) => {
              const nextTaskData = { ...taskData };

              nextTaskData.results = taskData.results.map((item) => {
                if (item.id !== error.subTaskLocalId) return item;
                return { ...item, taskId: null, status: 'error' };
              });

              if (nextTaskData.results.every((item) => item.status === 'error')) {
                allTaskFailed = true;
                nextTaskData.taskId = null;
                nextTaskData.status = 'idle';
              }

              return nextTaskData;
            });
          } else {
            allTaskFailed = true;
            updateTask(taskLocalId, null);
          }

          // 失败以后重新请求account接口计算算力
          syncVipInfo();

          if (allTaskFailed) {
            reject();
          }

          notifyError(translate('generator_failed'), error.message || error.msg || 'Unknown Error');
        },
        onUpdate: handleTaskUpdate(taskLocalId),
        onSubTaskComplete: (subTaskLocalId, subTaskIndex, result, subTaskProjectData) => {
          let allTaskComplete = false;

          // 更新任务状态
          updateTask(taskLocalId, (taskData) => {
            const updateData = {
              results: taskData?.results?.map((item) => {
                if (item.id !== subTaskLocalId) return item;
                return Object.assign({}, item, { status: 'idle' });
              }),
            };

            if (updateData.results.every((item) => idleStatus[item.status])) {
              updateData.error = null;
              updateData.taskId = null;
              updateData.status = 'idle';

              allTaskComplete = true;
            }

            return updateData;
          });

          // 将生成结果插入到画布

          const videoResults = (result.videos || []).map((item) => ({ ...item, type: 'video' }));
          const imageResults = (result.images || []).map((item) => ({ ...item, type: 'image' }));

          applyGenerateResult(taskLocalId, videoResults.concat(imageResults), {
            taskId: result.generateId,
            subTaskIndex,
            width: projectData.renderWidth,
            height: projectData.renderHeight,
            projectData: subTaskProjectData,
            isVideos: !!result.videos,
          });

          // 成功以后重新请求account接口计算算力
          syncVipInfo();

          if (allTaskComplete) {
            resolve();
          }
        },
      })
        .then((result) => {
          console.log(`[DEBUG] generate image result 1:`, result);
          if (result) resolve(result);
        })
        .catch((error) => {
          console.log(`[DEBUG] generate image error 1:`, error);
          reject(error);
        });
    });

    if (!fullPayload.subTasks) {
      if (result) {
        const videoResults = (result.videos || []).map((item) => ({ ...item, type: 'video' }));
        const imageResults = (result.images || []).map((item) => ({ ...item, type: 'image' }));

        applyGenerateResult(taskLocalId, videoResults.concat(imageResults), {
          taskId: taskId,
          width: projectData.renderWidth,
          height: projectData.renderHeight,
          isVideos: !!result.videos,
          projectData,
        });

        eventBus.emit(EVENT_LABEL_HISTORY_RELOAD);
      }
    }
  } catch (error) {
    console.error(`[DEBUG] generate image error 2:`, error);
    updateTask(taskLocalId, { status: 'error', error: error?.message || error?.msg || 'Unknown Error' });
    notifyError(translate('generator_failed'), error?.message || error?.msg || 'Unknown Error');
  }
};

export const applyGenerateResult = async (taskLocalId, results, params) => {
  const { tasks } = getProjectState();
  const taskData = tasks.map[taskLocalId];

  if (!taskData) return;

  console.log(`[DEBUG] applyGenerateResult`, results);

  const generateResultHandler = generateResultHandlers[taskData.type] || generateResultHandlers.generator;

  if (generateResultHandler) {
    const res = await generateResultHandler(taskData, results, params);

    eventBus.emit(EVENT_LABEL_HISTORY_RELOAD);

    if (res && res.results) {
      eventBus.emit(`task_finish_${taskData.unitId}`, res);
    }

    // 生图成功，检查是否弹出nps问卷
    checkAndPopNps();
  } else {
    // throw new Error(`Unknown generate result handler: ${taskData.type}`);
  }
};

const fixedFieldsMap = {
  'CFG scale': 'cfgScale',
  'Negative prompt': 'negativePrompt',
  Steps: 'samplingStep',
  Sampler: 'samplingMethod',
  Seed: 'seed',
};

const fixFields = (genInfo) => {
  genInfo = Object.keys(genInfo).reduce((acc, key) => {
    const fixedKey = fixedFieldsMap[key] || key;
    acc[fixedKey] = genInfo[key];
    return acc;
  }, {});

  return genInfo;
};

export const detailInfoToGenerateInfo = (details) => {
  try {
    const { width, height, baseModel, loraModes, extraInfo } = details;
    let genInfo = JSON.parse(extraInfo);
    genInfo = {
      ...genInfo,
      ...(genInfo.text2img || genInfo.text2img || {}),
    };

    const { cfgScale, samplingMethod, samplingStep, seed, prompt, negativePrompt } = fixFields(genInfo);
    const params = { cfgScale, samplingSteps: samplingStep, seed, prompt, negativePrompt, width, height };

    if (samplingMethod) {
      if (typeof samplingMethod === 'number') {
        params.samplerMethod = samplingMethod;
      } else {
        params.samplerMethod =
          samplerMethodOptions.find((i) => i.label === samplingMethod)?.value || samplerMethodOptions[0].value;
      }
    }

    if (baseModel) {
      const appliedModel = {
        name: `${baseModel.name || ''}_${baseModel.versionName || ''}`,
        uuid: baseModel.versionUuid || baseModel.uuid,
        versionId: baseModel?.versionId,
        versionName: baseModel.versionName,
        baseType: BASETYPE_NAME_TO_VALUE[baseModel.baseType] || 'SD XL',
        modelType: 'checkpoint',
        imageUrl: baseModel.imageUrl,
        modelId: baseModel.modelId || baseModel.id,
        modelUuid: baseModel.modelUuid,
        userAvatar: baseModel.userAvatar,
        userName: baseModel.userName,
        runCount: baseModel.runCount || undefined,
        modelName: baseModel.modelName || baseModel.name,
        vipUsed: baseModel.vipUsed,
      };

      params.mode = 1;
      params.baseType = baseModel.baseType || MODEL_SDXL;
      params.checkpoint = appliedModel;
    } else {
      params.checkpoint = null;
      params.baseType = MODEL_SDXL;
    }

    if (Array.isArray(loraModes)) {
      params.loraModels = loraModes.map((item) => {
        return {
          name: `${item.name || ''}_${item.versionName || ''}`,
          uuid: item.uuid || item.versionUuid,
          versionId: item?.versionId,
          baseType: BASETYPE_NAME_TO_VALUE[item.baseType] || 'SD XL',
          modelType: 'lora',
          imageUrl: item.imageUrl,
          userAvatar: item.userAvatar,
          userName: item.userName,
          runCount: item.runCount || undefined,
          modelId: item.modelId || item.id,
          modelUuid: item.modelUuid,
          modelName: item.name || item.modelName,
          weight: 0.7,
          vipUsed: item.vipUsed,
        };
      });
    } else {
      params.loraModels = [];
    }
    return params;
  } catch (error) {
    return {};
  }
};
